import type { ReactNode } from "react";
import {
  CalendarIcon,
  ChartBarIcon,
  FolderIcon,
  MailIcon,
  SearchIcon,
  UsersIcon,
  VideoCameraIcon,
} from "@heroicons/react/outline";

type EmptyStateIcon = "search" | "calendar" | "users" | "chart" | "folder" | "session" | "mail";

interface EmptyStateProps {
  icon?: EmptyStateIcon;
  title: string;
  description?: string;
  action?: {
    label: string;
    onClick: () => void;
  };
  children?: ReactNode;
  className?: string;
}

const iconComponents: Record<EmptyStateIcon, JSX.Element> = {
  search: <SearchIcon className="h-12 w-12" strokeWidth={1.5} />,
  calendar: <CalendarIcon className="h-12 w-12" strokeWidth={1.5} />,
  users: <UsersIcon className="h-12 w-12" strokeWidth={1.5} />,
  chart: <ChartBarIcon className="h-12 w-12" strokeWidth={1.5} />,
  folder: <FolderIcon className="h-12 w-12" strokeWidth={1.5} />,
  session: <VideoCameraIcon className="h-12 w-12" strokeWidth={1.5} />,
  mail: <MailIcon className="h-12 w-12" strokeWidth={1.5} />,
};

/**
 * Empty state component for when there's no data to display.
 * Provides contextual messaging and optional action button.
 */
const EmptyState = ({
  icon = "chart",
  title,
  description,
  action,
  children,
  className = "",
}: EmptyStateProps) => {
  return (
    <div
      className={`flex flex-col items-center justify-center py-12 px-6 text-center ${className}`}
    >
      <div className="text-gray-300 mb-4">{iconComponents[icon]}</div>

      <h3 className="text-lg font-medium text-gray-900 mb-2">{title}</h3>

      {description && <p className="text-sm text-gray-500 max-w-sm mb-4">{description}</p>}

      {action && (
        <button
          onClick={action.onClick}
          className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
        >
          {action.label}
        </button>
      )}

      {children}
    </div>
  );
};

export default EmptyState;
